import {
  PACKING_RECOMMENDATION_APPLIED,
  PACKING_RECOMMENDATION_CLEARED,
  PACKING_RECOMMENDATION_DISMISSED,
  PACKING_RECOMMENDATION_FAILED,
  PACKING_RECOMMENDATION_FTU_ACTIVE_IMPORT_OPENED,
  PACKING_RECOMMENDATION_FTU_CLOSED,
  PACKING_RECOMMENDATION_FTU_COMPLETED,
  PACKING_RECOMMENDATION_FTU_COMPLETE_FAILED,
  PACKING_RECOMMENDATION_FTU_CONTINUE_CONFIRMATION_ACCEPTED,
  PACKING_RECOMMENDATION_FTU_CONTINUE_CONFIRMATION_DISMISSED,
  PACKING_RECOMMENDATION_FTU_CONTINUE_CONFIRMATION_SHOWN,
  PACKING_RECOMMENDATION_FTU_EXPORT_PRODUCTS_CLICKED,
  PACKING_RECOMMENDATION_FTU_IMPORT_ANOTHER_CLICKED,
  PACKING_RECOMMENDATION_FTU_IMPORT_RESULT_VIEWED,
  PACKING_RECOMMENDATION_FTU_OPENED,
  PACKING_RECOMMENDATION_FTU_STEP_BACK_CLICKED,
  PACKING_RECOMMENDATION_FTU_STEP_CONTINUED,
  PACKING_RECOMMENDATION_FTU_STEP_LINK_CLICKED,
  PACKING_RECOMMENDATION_FTU_STEP_SKIPPED,
  PACKING_RECOMMENDATION_OVERRIDDEN,
  PACKING_RECOMMENDATION_REJECTED,
  PACKING_RECOMMENDATION_REQUESTED,
  PACKING_RECOMMENDATION_RERUN_CLICKED,
  PACKING_RECOMMENDATION_RETRY_CLICKED,
  PACKING_RECOMMENDATION_SETTINGS_AUTO_APPLY_TOGGLED,
  PACKING_RECOMMENDATION_SETTINGS_AUTOMATION_RULES_LINK_CLICKED,
  PACKING_RECOMMENDATION_SETTINGS_ENFORCEMENT_MODE_CHANGED,
  PACKING_RECOMMENDATION_SETTINGS_OPTIMIZATION_FACTOR_CHANGED,
  PACKING_RECOMMENDATION_SHOWN,
  PACKING_RECOMMENDATION_WARNING_ACKNOWLEDGED,
} from '../../constants/analytics-events';
import type { PackingEnforcementMode } from '../enforcement/constants';
import { Segment } from '../../services/analytics-services/segment';

export const trackPackingRecommendationRequested = (fulfillmentPlanId: string, optimizationFactor: string): void => {
  Segment.trackEvent(PACKING_RECOMMENDATION_REQUESTED, {
    fulfillmentPlanId,
    optimizationFactor,
  });
};

export const trackPackingRecommendationShown = (
  fulfillmentPlanId: string,
  packageName: string,
  isMultiBox: boolean,
  boxCount: number,
): void => {
  Segment.trackEvent(PACKING_RECOMMENDATION_SHOWN, {
    fulfillmentPlanId,
    packageName,
    isMultiBox,
    boxCount,
  });
};

export const trackPackingRecommendationApplied = (
  fulfillmentPlanId: string,
  packageName: string,
  isMultiBox: boolean,
  boxCount: number,
): void => {
  Segment.trackEvent(PACKING_RECOMMENDATION_APPLIED, {
    fulfillmentPlanId,
    packageName,
    isMultiBox,
    boxCount,
  });
};

export const trackPackingRecommendationDismissed = (fulfillmentPlanId: string, packageName: string): void => {
  Segment.trackEvent(PACKING_RECOMMENDATION_DISMISSED, {
    fulfillmentPlanId,
    packageName,
  });
};

export const trackPackingRecommendationCleared = (fulfillmentPlanId: string, packageName: string): void => {
  Segment.trackEvent(PACKING_RECOMMENDATION_CLEARED, {
    fulfillmentPlanId,
    packageName,
  });
};

export const trackPackingRecommendationRejected = (fulfillmentPlanId: string, packageName: string): void => {
  Segment.trackEvent(PACKING_RECOMMENDATION_REJECTED, {
    fulfillmentPlanId,
    packageName,
  });
};

export const trackPackingRecommendationRerunClicked = (fulfillmentPlanId: string, optimizationFactor: string): void => {
  Segment.trackEvent(PACKING_RECOMMENDATION_RERUN_CLICKED, {
    fulfillmentPlanId,
    optimizationFactor,
  });
};

export const trackPackingRecommendationRetryClicked = (fulfillmentPlanId: string, optimizationFactor: string): void => {
  Segment.trackEvent(PACKING_RECOMMENDATION_RETRY_CLICKED, {
    fulfillmentPlanId,
    optimizationFactor,
  });
};

export const trackPackingRecommendationFailed = (fulfillmentPlanId: string, failureReason: string): void => {
  Segment.trackEvent(PACKING_RECOMMENDATION_FAILED, {
    fulfillmentPlanId,
    failureReason,
  });
};

// The free-text override reason is never sent to Segment — only the enforcement mode and outcome.
export const trackPackingRecommendationOverridden = (fulfillmentPlanId: string, mode: PackingEnforcementMode): void => {
  Segment.trackEvent(PACKING_RECOMMENDATION_OVERRIDDEN, {
    fulfillmentPlanId,
    mode,
  });
};

export const trackPackingRecommendationWarningAcknowledged = (fulfillmentPlanId: string): void => {
  Segment.trackEvent(PACKING_RECOMMENDATION_WARNING_ACKNOWLEDGED, {
    fulfillmentPlanId,
  });
};

// SettingsGrid renders both on the packing settings page and in the FTU wizard preferences step.
export type PackingRecommendationSettingsSource = 'settingsPage' | 'ftuWizard';

export const trackPackingRecommendationSettingsOptimizationFactorChanged = (
  source: PackingRecommendationSettingsSource,
  previousValue: string,
  newValue: string,
): void => {
  Segment.trackEvent(PACKING_RECOMMENDATION_SETTINGS_OPTIMIZATION_FACTOR_CHANGED, { source, previousValue, newValue });
};

export const trackPackingRecommendationSettingsEnforcementModeChanged = (
  source: PackingRecommendationSettingsSource,
  previousValue: PackingEnforcementMode,
  newValue: PackingEnforcementMode,
): void => {
  Segment.trackEvent(PACKING_RECOMMENDATION_SETTINGS_ENFORCEMENT_MODE_CHANGED, { source, previousValue, newValue });
};

export const trackPackingRecommendationSettingsAutoApplyToggled = (
  source: PackingRecommendationSettingsSource,
  isEnabled: boolean,
): void => {
  Segment.trackEvent(PACKING_RECOMMENDATION_SETTINGS_AUTO_APPLY_TOGGLED, { source, isEnabled });
};

export const trackPackingRecommendationSettingsAutomationRulesLinkClicked = (source: PackingRecommendationSettingsSource): void => {
  Segment.trackEvent(PACKING_RECOMMENDATION_SETTINGS_AUTOMATION_RULES_LINK_CLICKED, { source });
};

export type PackingRecommendationFtuOpenTrigger = 'coldStateBanner' | 'deepLink';

export const trackPackingRecommendationFtuOpened = (
  trigger: PackingRecommendationFtuOpenTrigger,
  isRerun: boolean,
  hasStarted: boolean,
): void => {
  Segment.trackEvent(PACKING_RECOMMENDATION_FTU_OPENED, { trigger, isRerun, hasStarted });
};

export const trackPackingRecommendationFtuClosed = (stepId: string): void => {
  Segment.trackEvent(PACKING_RECOMMENDATION_FTU_CLOSED, { stepId });
};

export const trackPackingRecommendationFtuStepContinued = (stepId: string): void => {
  Segment.trackEvent(PACKING_RECOMMENDATION_FTU_STEP_CONTINUED, { stepId });
};

export const trackPackingRecommendationFtuStepBackClicked = (stepId: string): void => {
  Segment.trackEvent(PACKING_RECOMMENDATION_FTU_STEP_BACK_CLICKED, { stepId });
};

export const trackPackingRecommendationFtuStepSkipped = (stepId: string): void => {
  Segment.trackEvent(PACKING_RECOMMENDATION_FTU_STEP_SKIPPED, { stepId });
};

export const trackPackingRecommendationFtuStepLinkClicked = (stepId: string): void => {
  Segment.trackEvent(PACKING_RECOMMENDATION_FTU_STEP_LINK_CLICKED, { stepId });
};

export const trackPackingRecommendationFtuCompleted = (
  optimizationFactor: string,
  enforcementMode: PackingEnforcementMode,
  skippedStepIds: string[],
): void => {
  Segment.trackEvent(PACKING_RECOMMENDATION_FTU_COMPLETED, { optimizationFactor, enforcementMode, skippedStepIds });
};

export const trackPackingRecommendationFtuCompleteFailed = (): void => {
  Segment.trackEvent(PACKING_RECOMMENDATION_FTU_COMPLETE_FAILED);
};

export const trackPackingRecommendationFtuContinueConfirmationShown = (): void => {
  Segment.trackEvent(PACKING_RECOMMENDATION_FTU_CONTINUE_CONFIRMATION_SHOWN);
};

export const trackPackingRecommendationFtuContinueConfirmationAccepted = (): void => {
  Segment.trackEvent(PACKING_RECOMMENDATION_FTU_CONTINUE_CONFIRMATION_ACCEPTED);
};

export const trackPackingRecommendationFtuContinueConfirmationDismissed = (): void => {
  Segment.trackEvent(PACKING_RECOMMENDATION_FTU_CONTINUE_CONFIRMATION_DISMISSED);
};

export const trackPackingRecommendationFtuExportProductsClicked = (): void => {
  Segment.trackEvent(PACKING_RECOMMENDATION_FTU_EXPORT_PRODUCTS_CLICKED);
};

export const trackPackingRecommendationFtuActiveImportOpened = (importType: string): void => {
  Segment.trackEvent(PACKING_RECOMMENDATION_FTU_ACTIVE_IMPORT_OPENED, { importType });
};

export const trackPackingRecommendationFtuImportResultViewed = (importType: string): void => {
  Segment.trackEvent(PACKING_RECOMMENDATION_FTU_IMPORT_RESULT_VIEWED, { importType });
};

export const trackPackingRecommendationFtuImportAnotherClicked = (importType: string, previousImportStatus: string): void => {
  Segment.trackEvent(PACKING_RECOMMENDATION_FTU_IMPORT_ANOTHER_CLICKED, { importType, previousImportStatus });
};
